import { useCallback, useMemo, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { selectCompany } from "../../homeSlice";
import { selectDepartments } from "../departmentsSlice";
import { DialogModalDS } from "@qivia/ui/src/designSystem/components/DialogModal";
import { ButtonDS, SelectDS, Spacer, Tag, TextCapitalized } from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { Car } from "@qivia/ui/src/assets/assets";
import { t } from "i18next";
import styled from "styled-components";
import { listAllVehiclesInDbAsync, selectListAllVehiclesInDb, selectListAllVehiclesInDbStatus, updateVehicleDepartmentAsync } from "../../vehicles/vehiclesSlice";
import { Option } from "@qivia/ui/src/designSystem/components/Select";

export type FormAddVehicleToDepartment = {
    vehicleReference: string;
    vehicleRegistrationNumber: string;
    vehicleBrand: string;
    vehicleModel: string;
};

export const AddVehicleToDepartmentModal = (props: {
    departmentUuid: string;
    visible: boolean;
    onCloseModal: () => void;
}) => {
    const dispatch = useAppDispatch();
    const onCloseModal = props.onCloseModal;
    const company = useAppSelector(selectCompany);
    const departments = useAppSelector(selectDepartments);
    const vehicles = useAppSelector(selectListAllVehiclesInDb);
    const getVehiclesByCompanyStatus = useAppSelector(
        selectListAllVehiclesInDbStatus,
    );

    const [selectedVehicleUuid, setSelectedVehicleUuid] = useState<string>("");

    useEffect(() => {
        if (company && getVehiclesByCompanyStatus === "idle") {
            void dispatch(listAllVehiclesInDbAsync({ companyUuid: company.uuid }));
        }
    }, [company, dispatch, getVehiclesByCompanyStatus]);

    const department = useMemo(() => {
        if (departments) {
            return departments.find((department) => department.uuid === props.departmentUuid);
        }
    }, [departments, props.departmentUuid]);

    const addVehicle = useCallback(() => {
        if (!selectedVehicleUuid || !department || !company) return;

        void dispatch(updateVehicleDepartmentAsync({
            vehicleUuid: selectedVehicleUuid,
            departmentName: department.name,
            companyUuid: company.uuid,
        }));
        
        void onCloseModal();
    }, [company, department, dispatch, onCloseModal, selectedVehicleUuid]);

    const resetValue = useCallback(() => {
        setSelectedVehicleUuid("");
    }, [setSelectedVehicleUuid]);

    const optionsVehicles = useMemo(() => {
        const vehiclesNotInDepartment = vehicles.filter((vehicle) => vehicle.department !== department?.uuid);

        return vehiclesNotInDepartment.map((vehicle) => {
            return {
                value: vehicle.uuid,
                label: <StyledVehicleItem key={vehicle.uuid}>
                    {vehicle.reference && (
                        <>
                            <Tag
                                textColor={colors["colors/text/darkGrey"]}
                                text={vehicle.reference}
                                backgroundColor={
                                colors["colors/surfaces/background/background_level0"]
                                }
                                borderColor={colors["colors/text/veryLightGrey"]}
                            />
                            <Spacer x={1} />
                        </>
                    )}
                    {vehicle.registrationNumber && (
                        <>
                            {!vehicle.reference && <Spacer x={0.25} />}
                            <TextCapitalized>{vehicle.registrationNumber || ""}</TextCapitalized>
                            <Spacer x={1} />
                        </>
                    )}
                    {vehicle.brand && (
                        <>
                            <TextCapitalized>{vehicle.brand || ""}</TextCapitalized>
                            <Spacer x={0.5} />
                        </>
                    )}
                    {vehicle.model && (
                        <>
                            <TextCapitalized>{vehicle.model || ""}</TextCapitalized>
                            <Spacer x={0.5} />
                        </>
                    )}
                    </StyledVehicleItem>
            }
        })
    }, [department?.uuid, vehicles])

    const handleCloseModal = () => {
        resetValue();
        props.onCloseModal();
    }

    if (!department) return

    return (
        <DialogModalDS
            visible={props.visible}
            title={t("departments.addVehicleModal.title") || ""}
            subTitle={(t("departments.addVehicleModal.subtitle", { departmentName: department.name }))}
            iconTitle={
                <StyledIcon>
                    <Car />
                </StyledIcon>}
            onClose={handleCloseModal}
            bottomButtons={{
                leftButton: (
                    <ButtonDS
                        text={t("departments.addVehicleModal.cancel")}
                        format={"fill"}
                        buttonType={"secondary"}
                        onClick={handleCloseModal}
                    />
                ),
                rightButton: (
                    <ButtonDS
                        text={t("departments.addVehicleModal.confirm")}
                        format={"fill"}
                        buttonType={"primary"}
                        onClick={addVehicle}
                        disabled={!selectedVehicleUuid}
                    />
                ),
            }}
        >
        <SelectDS
            customHeight={3}
            label={""}
            placeholder={t("departments.addVehicleModal.placeholder") || ""}
            value={optionsVehicles.find((option) => option.value === selectedVehicleUuid) ?? null}
            options={optionsVehicles}
            onChange={(selectedOption) => setSelectedVehicleUuid(selectedOption?.value ?? "")}
            filterOption={(option: Option<string> | null, inputValue: string) => {
                const vehicle = vehicles.find((v) => v.uuid === option?.value);
                if (!vehicle) return false;
                const input = inputValue.toLowerCase().trim();
                return (
                    vehicle.reference?.toLowerCase().includes(input) ||
                    vehicle.registrationNumber?.toLowerCase().includes(input) ||
                    vehicle.brandAndModel?.toLowerCase().includes(input) ||
                    vehicle.model?.toLowerCase().includes(input) || false
                );
            }}
            emptyErrorMessage={t("departments.addVehicleModal.emptyErrorMessage") || ""}
            withoutSpacerBottom
            allWidth
        />
        </DialogModalDS>
    );
}

const StyledIcon = styled.div`
  background-color: ${colors["colors/surfaces/background/background_level2"]};
  color: ${colors["colors/text/black"]};
`;

const StyledVehicleItem = styled.div`
    display: flex;
    align-items: center;
`;